import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { BaseQLFieldDefinition } from '@adornis/baseql/metadata/types.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import '@adornis/buchungsheld-theme/components/tr-dropdown-selection.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/components/tr-input.js';
import '@adornis/buchungsheld-theme/components/tr-tooltip.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import type { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import { isFieldMandatory } from '@adornis/cms/client/generic-field-formcontroller.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { until } from 'lit/directives/until.js';
import {
  ConfirmEnum,
  MentoringMentee,
  MentoringMentor,
  NaechsteKarriereschritteEnum,
  NeededFromMentorEnum,
} from '../entities/mentoring-user.js';
import type { TranslationDictionaryMentoring } from '../translations.js';
import './components/address.js';
import './components/mentoring-ffm-inline-selection.js';

export function mentorCustomFieldMapping(
  field: BaseQLFieldDefinition,
  form: GenericFieldFormController<MentoringMentor>,
  translation: TranslationController<TranslationDictionaryMentoring>,
  parent?: ChemistryLitElement,
) {
  const dynamicNonOptionalAsterisk = isFieldMandatory(field, form.document!.constructor as typeof AdornisEntity).then(
    isMandatory => (isMandatory ? '*' : ''),
  );
  switch (field.name) {
    case 'privacyPolicy':
      const trans = translation.translate('field', 'privacyPolicy');
      return html`
        <tr-checkbox
          .label=${html`${unsafeHTML(
            trans
              .replace('Datenschutzerklärung', '<a href="/data-privacy" target="_blank"> Datenschutzerklärung</a>')
              .replace('privacy agreement', '<a href="/data-privacy" target="_blank"> privacy agreement</a>'),
          )}`}
          ${form.field(field.name)}
          ${css({
            gridColumn: '1 / -1',
          })}
        ></tr-checkbox>
      `;
    case 'workIntegratedLeaning':
      return inlineEnum(field, form, translation, { show: true }, parent);
    case 'address':
      return html`
        <tr-flex flex ${css({ gridColumn: '1 / -1' })} space="md">
          <tr-flex>
            <tr-input
              placeholder=${translation.translate('field', 'streetAndHousenumber')}
              ${form.field('address', 'streetAndHousenumber')}
            ></tr-input>
          </tr-flex>
          <tr-flex horizontal space="sm" flex>
            <tr-input
              placeholder=${translation.translate('field', 'zip')}
              ${form.field('address', 'zipCity', 'zip')}
            ></tr-input>
            <tr-input
              flex
              placeholder=${translation.translate('field', 'city')}
              ${form.field('address', 'zipCity', 'city')}
            ></tr-input>
          </tr-flex>
        </tr-flex>
      `;
    case 'age':
      const humanReadable = translation.translate('field', 'age');
      return html`
        <tr-flex horizontal crossaxis-center flex space="md" ${css({ gridColumn: '1 / -1' })}>
          <tr-input
            flex
            placeholder=${until(
              dynamicNonOptionalAsterisk.then(asterisk => `${humanReadable}${asterisk}`),
              humanReadable,
            )}
            placeholder-mode=${'static-floating'}
            type="number"
            step="1"
            min="1"
            max="200"
            ${form.field(field.name)}
          ></tr-input>
        </tr-flex>
      `;
    case 'goetheStudent':
    case 'abschlussarbeitBetreuen':
    case 'international':
    case 'genderPreference':
    case 'internationalMentees':
    case 'meetingType':
      return inlineEnum(field, form, translation, { show: false }, parent);
    default:
      return undefined;
  }
}

export function menteeCustomFieldMapping(
  field: BaseQLFieldDefinition,
  form: GenericFieldFormController<MentoringMentee>,
  translation: TranslationController<TranslationDictionaryMentoring>,
  parent?: ChemistryLitElement,
) {
  const ignoreFields = ['minSemesterPrerequisiteSonstiges', 'expectedGraduationMonth'];
  const dynamicNonOptionalAsterisk = isFieldMandatory(field, form.document!.constructor as typeof AdornisEntity).then(
    isMandatory => (isMandatory ? '*' : ''),
  );

  if (ignoreFields.includes(field.name)) return nothing;
  switch (field.name) {
    case 'languageC1':
      return inlineEnum(field, form, translation, { show: true }, parent);
    case 'minSemesterPrerequisite':
      return html`
        <tr-text> ${translation.translate('field', 'minSemesterPrerequisite')} </tr-text>
        <tr-flex space="xs" center>
          ${form.document?.minSemesterPrerequisite === ConfirmEnum.NEIN
            ? html`
                <tr-text tone="error">
                  ${translation.translate('field_description', 'minSemesterPrerequisiteInfo')}
                </tr-text>
              `
            : nothing}
          ${inlineEnum(
            field,
            form,
            translation,
            {
              show: [ConfirmEnum.JA],
            },
            parent,
          )[1]}
        </tr-flex>
      `;
    case 'extraSubject':
      return inlineEnum(
        field,
        form,
        translation,
        {
          show: [ConfirmEnum.JA],
        },
        parent,
      );
    case 'genderPreference':
    case 'meetingType':
    case 'firstGenerationStudent':
    case 'immigrantHistory':
    case 'studiedAbroad':
    case 'erasmusStudent':
    case 'searchingWork':
      return inlineEnum(field, form, translation, { show: false }, parent);
    case 'motivationMentee':
      return textField(field, form, translation, 200, parent);
    case 'discoveredFrom':
      return textField(field, form, translation);
    case 'profilePicture':
    case 'curriculumVitae':
      return nothing;
    case 'privacyPolicy':
      const trans = translation.translate('field', 'privacyPolicy');
      return html`
        <tr-checkbox
          .label=${html`${unsafeHTML(
            trans
              .replace('Datenschutzerklärung', '<a href="/data-privacy" target="_blank"> Datenschutzerklärung</a>')
              .replace('privacy agreement', '<a href="/data-privacy" target="_blank"> privacy agreement</a>'),
          )}`}
          ${form.field(field.name)}
          ${css({
            gridColumn: '1 / -1',
          })}
        ></tr-checkbox>
      `;
    case 'angestrebterAbschluss':
      const infoTranslation = translation.translate('field_description', field.name);
      return html`
        ${form.genericField([field.name], {
          placeholderMode: 'static-floating',
          info: infoTranslation !== `field_description.${field.name}` ? infoTranslation : undefined,
        })}
        ${form.genericField([field.name + 'Sonstiges'], {
          placeholderMode: 'static-floating',
          info: infoTranslation !== `field_description.${field.name}` ? infoTranslation : undefined,
        })}
      `;
    case 'expectedGraduationYear':
      const humanReadableYear = translation.translate('field', 'expectedGraduationYear');
      const humanReadableMonth = translation.translate('field', 'expectedGraduationMonth');
      return html`
        <tr-flex horizontal crossaxis-center flex space="md">
          <tr-text> ${translation.translate('field', 'expectedGraduation')} </tr-text>
        </tr-flex>
        </tr-flex>
          <tr-flex horizontal crossaxis-center flex space="md">
            <tr-input 
            placeholder=${until(
              dynamicNonOptionalAsterisk.then(asterisk => `${humanReadableYear}${asterisk}`),
              humanReadableYear,
            )} 
            placeholder-mode=${'static-floating'} type="number" step="1" ${form.field(field.name)}></tr-input>
            <tr-input 
            placeholder=${until(
              dynamicNonOptionalAsterisk.then(asterisk => `${humanReadableMonth}${asterisk}`),
              humanReadableMonth,
            )}
            placeholder-mode=${'static-floating'}
            min="1"
            max="12"
            step="1"
            type="number" ${form.field('expectedGraduationMonth')}></tr-input>
          </tr-flex>
        `;
    case 'naechsteKarriereschritte':
      return selectionWithOther(
        field,
        form,
        translation,
        parent,
        NaechsteKarriereschritteEnum,
        NaechsteKarriereschritteEnum.SONSTIGES,
        false,
      );

    case 'whatIsNeededFromMentor':
      return selectionWithOther(
        field,
        form,
        translation,
        parent,
        NeededFromMentorEnum,
        NeededFromMentorEnum.SONSTIGES,
        true,
      );

    case 'matrikelNummer':
      return html`
        <tr-flex horizontal space="md" crossaxis-center>
          <tr-input
            flex
            type="number"
            placeholder="${translation.translate('field', 'matrikelNummer')}*"
            ${form.field('matrikelNummer')}
          ></tr-input>
        </tr-flex>
        <tr-flex horizontal space="md" crossaxis-center>
          <tr-text tone="subtle" ${css({ marginTop: `${this.baseFontSize}px` })}>
            ${translation.translate('field_description', 'matrikelNummerInfo')}
          </tr-text>
        </tr-flex>
      `;
    default:
      return undefined;
  }
}

const inlineEnum = (
  field: BaseQLFieldDefinition,
  form: GenericFieldFormController<MentoringMentee | MentoringMentor>,
  translation: TranslationController<TranslationDictionaryMentoring>,
  otherOptions?: {
    show?: boolean | string[];
    mode?: 'same-line' | 'next-line';
  },
  parent?: ChemistryLitElement,
) => {
  const type_info = _getTypeInfo(field);
  const sonstige = `${field.name}Sonstiges`;
  const sonstigeInfo = translation.translate('field_description', sonstige);
  const sonstigeInfoTranslation = sonstigeInfo !== `field_description.${sonstige}` ? sonstigeInfo : undefined;
  const sonstigeComponent = form.document?.hasOwnProperty(sonstige)
    ? form.genericField([sonstige], {
        placeholderMode: 'static-floating',
        info: sonstigeInfoTranslation !== 'field_description.' + sonstige ? sonstigeInfoTranslation : undefined,
      })
    : nothing;
  const showSonstige =
    typeof otherOptions?.show === 'boolean'
      ? otherOptions?.show
      : otherOptions?.show?.includes(form.document![field.name]);
  const sameLine = (otherOptions?.mode ?? 'same-line') === 'same-line';
  const infoTranslation = translation.translate('field_description', field.name);
  return [
    html`
      <tr-flex flex center>
        <tr-flex horizontal crossaxis-center space="md">
          <tr-text>
            ${translation.translate('field', field.name)}${until(
              isFieldMandatory(field, MentoringMentee).then(mandatory => (mandatory ? '*' : '')),
              '',
            )}
          </tr-text>
          ${infoTranslation !== `field_description.${field.name}`
            ? html`
                <tr-flex center ${css({ height: '9px', color: parent?.colors.neutral.shade(100) })}>
                  <tr-icon> info </tr-icon>
                </tr-flex>
              `
            : nothing}
        </tr-flex>
        ${infoTranslation !== `field_description.${field.name}`
          ? html` <tr-tooltip .text=${infoTranslation}> </tr-tooltip> `
          : nothing}
      </tr-flex>
    `,
    html`
      <tr-flex center>
        <tr-flex flex horizontal crossaxis-center center space="md">
          <mentoring-ffm-inline-selection
            no-flex
            .selectables=${type_info.values}
            ${form.field(field.name)}
            @value-picked=${(e: ValueEvent<string>) => {
              parent?.requestUpdate();
            }}
          ></mentoring-ffm-inline-selection>
          <tr-flex flex center space="xs"> ${showSonstige && sameLine ? sonstigeComponent : nothing} </tr-flex>
        </tr-flex>

        ${showSonstige && !sameLine ? sonstigeComponent : nothing}
      </tr-flex>
    `,
  ];
};

const textField = (
  field: BaseQLFieldDefinition,
  form: GenericFieldFormController<MentoringMentee>,
  translation: TranslationController<TranslationDictionaryMentoring>,
  minLength?: number,
  parent?: ChemistryLitElement,
) => {
  const isMandatory = ['motivationMentee'].includes(field.name);
  return html`
    <tr-flex flex space="md" center ${css({ gridColumn: '1 / -1' })}>
      <tr-flex flex horizontal center space="md">
        <tr-textarea
          flex
          type="text"
          placeholder="${translation.translate('field', field.name)}${isMandatory ? '*' : ''}"
          ${form.field(field.name)}
          @blur=${() => parent?.requestUpdate()}
        ></tr-textarea>
      </tr-flex>
      ${minLength
        ? html`
            <tr-text tone="subtle">
              Noch ${Math.max(0, minLength - (form.document?.[field.name] ?? '').length)} Zeichen
            </tr-text>
          `
        : nothing}
    </tr-flex>
  `;
};

const selectionWithOther = (
  field: BaseQLFieldDefinition,
  form: GenericFieldFormController<MentoringMentee | MentoringMentor>,
  translation: TranslationController<TranslationDictionaryMentoring>,
  parent: ChemistryLitElement,
  enumType: any,
  otherValue: any,
  multiple: boolean,
) => {
  const value = form.document?.[field.name];
  const isMandatory = ['naechsteKarriereschritte', 'whatIsNeededFromMentor'].includes(field.name);
  return html`
    <tr-flex horizontal crossaxis-center space="md" ${css({ gridColumn: '1 / -1' })}>
      <tr-flex flex horizontal crossaxis-center center space="md">
        ${multiple
          ? html`
              <tr-dropdown-multiselection
                placeholder="${translation.translate('field', field.name)}${isMandatory ? '*' : ''}"
                flex
                .selectables=${Object.keys(enumType)
                  .filter(key => !key.startsWith('_'))
                  .map(key => enumType[key])}
                .renderString=${(value: any) => translation.translate('enums', value)}
                .value=${value}
                @value-picked=${(e: ValueEvent<any>) => {
                  parent?.requestUpdate();
                }}
                ${form.field(field.name)}
              >
              </tr-dropdown-multiselection>
            `
          : html`
              <tr-dropdown-selection
                placeholder="${translation.translate('field', field.name)}${isMandatory ? '*' : ''}"
                flex
                .selectables=${Object.keys(enumType)
                  .filter(key => !key.startsWith('_'))
                  .map(key => enumType[key])}
                .renderString=${(value: any) => translation.translate('enums', value)}
                .value=${value}
                @value-picked=${(e: ValueEvent<any>) => {
                  parent?.requestUpdate();
                }}
                ${form.field(field.name)}
              >
              </tr-dropdown-selection>
            `}
      </tr-flex>
    </tr-flex>
    <tr-flex space="md" center ${css({ gridColumn: '1 / -1' })}>
      ${(multiple ? value && Array.isArray(value) && value.find(v => v === otherValue) : value == otherValue)
        ? html`
            ${form.genericField([`${field.name}Sonstiges`], {
              placeholderMode: 'static-floating',
            })}
          `
        : nothing}
    </tr-flex>
  `;
};

const _getTypeInfo = (field: BaseQLFieldDefinition): { type: string; name: string; values?: string[] } | undefined => {
  const type = field.type();
  if (type._isBaseQLEnum) {
    const values = Object.keys(type).filter(key => !key.startsWith('_'));
    return { name: type._class, values, type: 'enum' };
  } else if (type.name) {
    return { name: type.name, type: type.name.toLowerCase() };
  }
};

export const fieldOrderMentee: Array<keyof EntityData<MentoringMentee>> = [
  'firstName',
  'lastName',
  'profilePicture',
  'minSemesterPrerequisite',
  'minSemesterPrerequisiteSonstiges',
  'languageC1',
  'gender',
  'age',
  'staatsangehoerigkeit',
  'matrikelNummer',
  'email',
  'emailRepeat',
  'phone',
  'privacyPolicy',
  'preferredLanguage',
  'whatIsNeededFromMentor',
  'whatIsNeededFromMentorSonstiges',
  'department',
  'mainSubject',
  'extraSubject',
  'extraSubjectSonstiges',
  'menteeSemester',
  'angestrebterAbschluss',
  'angestrebterAbschlussSonstiges',
  'genderPreference',
  'meetingType',
  'sonstigesNotes',
  'motivationMentee',
  'firstTime',
  'professionField',
  'naechsteKarriereschritte',
  'naechsteKarriereschritteSonstiges',
  'searchingWork',
  'expectedGraduationYear',
  'expectedGraduationMonth',
  'firstGenerationStudent',
  'immigrantHistory',
  'studiedAbroad',
  'erasmusStudent',
  'businessProfile',
  'discoveredFrom',
  'curriculumVitae',
  'preferedMentors',
];

export const fieldOrderMentor: Array<keyof EntityData<MentoringMentor>> = [
  'firstName',
  'lastName',
  'profilePicture',
  'title',
  'gender',
  'birthday',
  'age',
  'email',
  'emailRepeat',
  'phone',
  'address',
  'privacyPolicy',
  'mentorSprache',
  'studium',
  'abschluss',
  'currentPosition',
  'currentOcupationFocus',
  'berufserfahrung',
  'sonstigesErfahrungen',
  'genderPreference',
  'meetingType',
  'motivationMentor',
  'menteeDepartments',
  'maxParallelMentees',
  'international',
  'internationalMentees',
  'goetheStudent',
  'businessProfile',
  'abschlussarbeitBetreuen',
  'workIntegratedLeaning',
  'workIntegratedLeaningSonstiges',
  'discoveredFrom',
  'curriculumVitae',
];
